import React, { useState } from "react"
import { Button, Col, Modal, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import EventBus from "../../common/EventBus"
import MatrixSelection from "../../common/pricing/add-item-modal/components/MatrixSelection"
import LoadingSpinner from "../../common/spinner/loading.component"
import { ProductSearchModalProps } from "../../common/types/productItemList"
import IntlProps from "../../state/helpers/IntlProps"
import { ProductItemDto } from "../../state/listing/product/models"
import Product from "../../state/product/Product"
import ProductService from "../../state/product/ProductService"
import { PRODUCT_TYPE } from "../../state/product/Types"
import { MatrixItemDto } from "../../state/product/matrix/matrix-item/MatrixItem"
import { ADD_ITEM_BUNDLE_MESSAGES, mapProductDtoToProductItem } from "../../utils"
import ProductItemList from "./ProductItemList"
import ProductSearchFilter from "./ProductSearchFilter"

const ProductSearchModal = (props: ProductSearchModalProps & IntlProps) => {
    const [searchKeywords, setSearchKeywords] = useState<string>("")
    const [filters, setFilters] = useState([])
    const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
    const [selectedMatrixProduct, setSelectedMatrixProduct] = useState<Product | undefined>(undefined)
    const [selectedMatrixItem, setSelectedMatrixItem] = useState<ProductItemDto | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(false)

    const handleKeywordsChange = (keywords) => {
        setSearchKeywords(keywords)
    }

    const handleFiltersChange = (items) => {
        setFilters(items)
    }

    const handleCloseModal = () => {
        props.isModalChangedCallback(true, [])
    }

    const handleSaveModal = () => {
        props.isModalChangedCallback(true, selectedItemIds)
    }

    const handleAddVariantModal = () => {
        if(selectedMatrixItem) {
            props.addItemCallback?.(selectedMatrixItem)
            displaySuccessNotification()
        }
    }

    const handleMultiSelect = (item: ProductItemDto) => {
        const filteredIds = selectedItemIds.filter(id => id != item.id)
        if (filteredIds.length != selectedItemIds.length) {
            setSelectedItemIds(filteredIds)
        } else {
            setSelectedItemIds([...selectedItemIds, item.id])
        }
    }

    const displaySuccessNotification = () => {
        return EventBus.dispatch(EventBus.TopicSendSuccessNotification, {
            message: <FormattedMessage
                key="product.components.product_search_modal.item_added_notification"
                id="product.components.product_search_modal.item_added_notification"
                defaultMessage="Item added to Bundle"
            />
        })
    }

    const getProductWithMatrix = async (itemId: string) => {
        setLoading(true)
        const itemSelected = await ProductService.getProduct(itemId)
        if(itemSelected instanceof Product){
            setSelectedMatrixProduct(itemSelected)
            setLoading(false)
        }
    }

    const displayMatrixItemSelection = () => {
        if(selectedMatrixProduct?.getMatrix() !== undefined) {
            return (<div className="container-fluid">
                <Row>
                    <Col className="pt-4 mb-2">
                        <MatrixSelection
                            product={selectedMatrixProduct}
                            onMatrixItemSelect={(product: MatrixItemDto) => {
                                if(product) {
                                    const newItem = mapProductDtoToProductItem(product.attributes.product)
                                    setSelectedMatrixItem(newItem)
                                } else {
                                    setSelectedMatrixItem(undefined)
                                }
                            }}
                        />
                    </Col>
                </Row>
            </div>)
        }
    }

    const handleAddProduct = (item: ProductItemDto) => {
        if (props.isMultiSelect) {
            handleMultiSelect(item)
            props.addItemCallback?.(item)
        } else {
            if(item.type === PRODUCT_TYPE.MATRIX_PARENT) {
                getProductWithMatrix(item.id)
            } else {
                props.addItemCallback?.(item)
                displaySuccessNotification()
            }
        }
    }


    return (
        <Modal show={true} onHide={handleCloseModal} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5 className="modal-title d-none d-md-block" id="productSearchModalLabel">
                        {!selectedMatrixProduct 
                            ? props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_add_item_title)
                            : props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_configure_item_title) + " " + selectedMatrixProduct?.title()
                        }
                    </h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!selectedMatrixProduct
                    ? <>
                        <ProductSearchFilter
                            searchKeywords={searchKeywords}
                            filters={filters}
                            onKeywordsChangeCallback={handleKeywordsChange}
                            onFiltersChangeCallback={handleFiltersChange}
                        />
                        <ProductItemList
                            searchKeywords={searchKeywords}
                            filters={filters}
                            productType={props.productType}
                            addProductCallback={handleAddProduct}
                            infoMessage={props.infoMessage}
                            isMultiSelect={props.isMultiSelect}
                            selectedItemIds={selectedItemIds}
                            additionalFilters={props.additionalFilters}
                        />
                    </>
                    : loading ? <LoadingSpinner className="pt-4" /> : displayMatrixItemSelection()
                }
            </Modal.Body>
            <Modal.Footer>
                <Col>
                    <Row className="d-flex justify-content-between flex-wrap">
                        <div className="d-flex justify-content-start">
                            {selectedMatrixProduct && <Button
                                className="ml-2"
                                variant="outline-primary"
                                onClick={() => {
                                    setSelectedMatrixProduct(undefined)
                                }}
                            >
                                {props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_back_btn)}
                            </Button>}
                        </div>
                        <div className="d-flex justify-content-end">
                            <Button
                                className="mr-2 btn-modal-lg"
                                variant="secondary"
                                onClick={handleCloseModal}
                            >
                                {props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_close_btn)}
                            </Button>
                            {selectedMatrixProduct
                                && <Button
                                    className="mr-2 btn-modal-lg"
                                    variant="primary"
                                    disabled={!selectedMatrixItem ? true : false}
                                    onClick={handleAddVariantModal} >
                                    {props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_add_variant_btn)}
                                </Button>
                            }
                            {props.isMultiSelect 
                                && <Button
                                    className="mr-2 btn-modal-lg"
                                    variant="primary"
                                    onClick={handleSaveModal}
                                >
                                    {props.intl.formatMessage(ADD_ITEM_BUNDLE_MESSAGES.modal_save_btn)}
                                </Button>

                            }
                        </div>
                    </Row>
                </Col>
            </Modal.Footer>
        </Modal>
    )
}

export default injectIntl(ProductSearchModal)



// WEBPACK FOOTER //
// ./src/product/components/ProductSearchModal.tsx